import React, { useState } from 'react';
import { Button, Typography, Space, Steps, Card, Select } from 'antd';
import { 
  BorderOutlined, 
  AimOutlined, 
  PlayCircleOutlined,
  RocketOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  GlobalOutlined,
  BuildOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';

const { Option } = Select;

const { Title, Text, Paragraph } = Typography;

// 视频 URL - 根据语言区分
const VIDEO_URLS = {
  en: {
    rectangle: 'https://d10unr08wxso0a.cloudfront.net/videos/Rectangle.MP4',
    point: 'https://d10unr08wxso0a.cloudfront.net/videos/Point.MP4',
    strategyBuilder: 'https://d10unr08wxso0a.cloudfront.net/videos/strategy_builder.mp4',
    aiAgent: 'https://d10unr08wxso0a.cloudfront.net/videos/ai_agent.mp4'
  },
  zh: {
    rectangle: 'https://d10unr08wxso0a.cloudfront.net/videos/Rectangle_ZH.MP4',
    point: 'https://d10unr08wxso0a.cloudfront.net/videos/Point_ZH.MP4',
    strategyBuilder: 'https://d10unr08wxso0a.cloudfront.net/videos/strategy_builder.mp4',
    aiAgent: 'https://d10unr08wxso0a.cloudfront.net/videos/ai_agent.mp4'
  }
};

const VideoPlayer: React.FC<{ type: 'rectangle' | 'point' | 'strategyBuilder' | 'aiAgent'; lang: string }> = ({ type, lang }) => {
  // 判断是否中文，默认英文
  const isZh = lang.startsWith('zh');
  const videoUrl = isZh ? VIDEO_URLS.zh[type] : VIDEO_URLS.en[type];
  
  return (
    <div
      style={{
        width: 'min(100%, 640px)',
        aspectRatio: '16/9',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
        background: '#000',
        margin: '0 auto'
      }}
    >
      <video
        key={`${type}-${isZh ? 'zh' : 'en'}`}  // key 包含语言，切换语言时也重新加载
        width="100%"
        height="100%"
        controls
        controlsList="nodownload"
        style={{
          borderRadius: '12px',
          objectFit: 'contain',
          display: 'block'
        }}
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

interface OnboardingPageProps {
  onComplete: () => void;
}

const OnboardingPage: React.FC<OnboardingPageProps> = ({ onComplete }) => {
  const { t, i18n } = useTranslation();
  const { theme } = useAppTheme();
  const [currentStep, setCurrentStep] = useState(0);

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value);
    localStorage.setItem('language', value);
  };

  const handleComplete = () => {
    onComplete();
  };

  const handleSkip = () => {
    onComplete();
  };

  // 步骤内容
  const steps = [
    {
      key: 'welcome',
      title: t('onboarding.welcomeStep'),
      content: (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          {/* Alpha 标签 */}
          <div
            style={{
              display: 'inline-block',
              padding: '8px 20px',
              background: `linear-gradient(135deg, ${theme.accentPrimary}20 0%, ${theme.accentPrimary}35 100%)`,
              borderRadius: '20px',
              marginBottom: '24px',
              border: `1px solid ${theme.accentPrimary}40`
            }}
          >
            <Text style={{ color: theme.accentPrimary, fontSize: '14px', fontWeight: 500 }}>
              {t('onboarding.alphaNotice')}
            </Text>
          </div>

          {/* Logo / 图标 */}
          <div
            style={{
              width: '100px',
              height: '100px',
              borderRadius: '24px',
              background: `linear-gradient(135deg, ${theme.accentPrimary}15 0%, ${theme.accentPrimary}40 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 28px',
              boxShadow: `0 12px 40px ${theme.accentPrimary}25`
            }}
          >
            <RocketOutlined style={{ fontSize: '48px', color: theme.accentPrimary }} />
          </div>

          {/* 主标题 */}
          <Title level={1} style={{ 
            color: theme.textPrimary, 
            marginBottom: '8px', 
            fontSize: '42px',
            fontWeight: 700,
            letterSpacing: '-0.5px'
          }}>
            {t('onboarding.welcomeTitle')}
          </Title>

          {/* 副标题 */}
          <Title level={3} style={{ 
            color: theme.accentPrimary, 
            marginBottom: '20px', 
            fontWeight: 500,
            opacity: 0.9
          }}>
            {t('onboarding.welcomeSubtitle')}
          </Title>

          {/* 描述 */}
          <Paragraph style={{ 
            color: theme.textSecondary, 
            fontSize: '16px', 
            maxWidth: '550px', 
            margin: '0 auto 40px',
            lineHeight: '1.8'
          }}>
            {t('onboarding.welcomeDescription')}
          </Paragraph>
          
          {/* 工具介绍标题 */}
          <Text style={{ 
            color: theme.textSecondary, 
            fontSize: '14px',
            display: 'block',
            marginBottom: '20px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            {t('onboarding.toolsIntro')}
          </Text>

          {/* 功能卡片 - 全部工具 */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '20px', 
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {[
              { icon: <BorderOutlined />, title: t('onboarding.feature1'), desc: t('onboarding.feature1Desc') },
              { icon: <AimOutlined />, title: t('onboarding.feature2'), desc: t('onboarding.feature2Desc') },
              { icon: <BuildOutlined />, title: t('onboarding.feature3'), desc: t('onboarding.feature3Desc') }
            ].map((feature, index) => (
              <div
                key={index}
                style={{
                  padding: '24px',
                  background: theme.surfaceBackground,
                  borderRadius: '16px',
                  border: `1px solid ${theme.surfaceBorder}`,
                  textAlign: 'left',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.15)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: `${theme.accentPrimary}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}
                >
                  <span style={{ fontSize: '24px', color: theme.accentPrimary }}>{feature.icon}</span>
                </div>
                <Text strong style={{ color: theme.textPrimary, fontSize: '16px', display: 'block', marginBottom: '8px' }}>
                  {feature.title}
                </Text>
                <Text style={{ color: theme.textSecondary, fontSize: '13px', lineHeight: '1.6' }}>
                  {feature.desc}
                </Text>
              </div>
            ))}
            
            {/* AI Quant Agent - 横跨整行 */}
            <div
              style={{
                gridColumn: '1 / -1',
                padding: '28px',
                background: `linear-gradient(135deg, ${theme.surfaceBackground} 0%, ${theme.accentPrimary}08 100%)`,
                borderRadius: '16px',
                border: `1px solid ${theme.accentPrimary}30`,
                textAlign: 'left',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'default',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.15)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* 装饰性光晕 */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                right: '-10%',
                width: '200px',
                height: '200px',
                background: `radial-gradient(circle, ${theme.accentPrimary}15 0%, transparent 70%)`,
                pointerEvents: 'none'
              }} />
              
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', position: 'relative' }}>
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '14px',
                    background: `linear-gradient(135deg, ${theme.accentPrimary}25 0%, ${theme.accentPrimary}50 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <RobotOutlined style={{ fontSize: '28px', color: theme.accentPrimary }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ color: theme.textPrimary, fontSize: '18px', display: 'block', marginBottom: '8px' }}>
                    {t('onboarding.feature4')}
                  </Text>
                  <Text style={{ color: theme.textSecondary, fontSize: '14px', lineHeight: '1.7' }}>
                    {t('onboarding.feature4Desc')}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'rectangle',
      title: t('onboarding.rectangleStep'),
      content: (
        <div style={{ display: 'flex', gap: '48px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* 视频区域 */}
          <div style={{ flex: '1 1 500px', minWidth: '300px' }}>
            <VideoPlayer type="rectangle" lang={i18n.language} />
          </div>
          
          {/* 说明区域 */}
          <div style={{ flex: '1 1 400px', minWidth: '280px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '14px',
                  background: `linear-gradient(135deg, ${theme.accentPrimary}20 0%, ${theme.accentPrimary}40 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <BorderOutlined style={{ fontSize: '28px', color: theme.accentPrimary }} />
              </div>
              <Title level={2} style={{ color: theme.textPrimary, margin: 0 }}>
                {t('tutorial.rectangleToolTitle')}
              </Title>
            </div>
            
            <Paragraph style={{ color: theme.textSecondary, fontSize: '16px', lineHeight: '1.8' }}>
              {t('tutorial.rectangleToolDescription')}
            </Paragraph>
            
            <Card
              style={{
                background: theme.appBackground,
                borderColor: theme.surfaceBorder,
                marginTop: '24px'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Text strong style={{ color: theme.textPrimary, display: 'block', marginBottom: '16px', fontSize: '15px' }}>
                {t('tutorial.howToUse')}
              </Text>
              <ol style={{ 
                color: theme.textSecondary, 
                paddingLeft: '20px', 
                margin: 0,
                lineHeight: '2.2',
                fontSize: '14px'
              }}>
                <li>{t('tutorial.rectangleStep1')}</li>
                <li>{t('tutorial.rectangleStep2')}</li>
                <li>{t('tutorial.rectangleStep3')}</li>
                <li>{t('tutorial.rectangleStep4')}</li>
              </ol>
            </Card>
          </div>
        </div>
      )
    },
    {
      key: 'point',
      title: t('onboarding.pointStep'),
      content: (
        <div style={{ display: 'flex', gap: '48px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* 视频区域 */}
          <div style={{ flex: '1 1 500px', minWidth: '300px' }}>
            <VideoPlayer type="point" lang={i18n.language} />
          </div>
          
          {/* 说明区域 */}
          <div style={{ flex: '1 1 400px', minWidth: '280px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '14px',
                  background: `linear-gradient(135deg, ${theme.accentPrimary}20 0%, ${theme.accentPrimary}40 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AimOutlined style={{ fontSize: '28px', color: theme.accentPrimary }} />
              </div>
              <Title level={2} style={{ color: theme.textPrimary, margin: 0 }}>
                {t('tutorial.pointToolTitle')}
              </Title>
            </div>
            
            <Paragraph style={{ color: theme.textSecondary, fontSize: '16px', lineHeight: '1.8' }}>
              {t('tutorial.pointToolDescription')}
            </Paragraph>
            
            <Card
              style={{
                background: theme.appBackground,
                borderColor: theme.surfaceBorder,
                marginTop: '24px'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Text strong style={{ color: theme.textPrimary, display: 'block', marginBottom: '16px', fontSize: '15px' }}>
                {t('tutorial.howToUse')}
              </Text>
              <ol style={{ 
                color: theme.textSecondary, 
                paddingLeft: '20px', 
                margin: 0,
                lineHeight: '2.2',
                fontSize: '14px'
              }}>
                <li>{t('tutorial.pointStep1')}</li>
                <li>{t('tutorial.pointStep2')}</li>
                <li>{t('tutorial.pointStep3')}</li>
                <li>{t('tutorial.pointStep4')}</li>
              </ol>
            </Card>
          </div>
        </div>
      )
    },
    {
      key: 'strategyBuilder',
      title: t('onboarding.strategyBuilderStep'),
      content: (
        <div style={{ display: 'flex', gap: '48px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* 视频区域 */}
          <div style={{ flex: '1 1 500px', minWidth: '300px' }}>
            <VideoPlayer type="strategyBuilder" lang={i18n.language} />
          </div>
          
          {/* 说明区域 */}
          <div style={{ flex: '1 1 400px', minWidth: '280px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '14px',
                  background: `linear-gradient(135deg, ${theme.accentPrimary}20 0%, ${theme.accentPrimary}40 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <BuildOutlined style={{ fontSize: '28px', color: theme.accentPrimary }} />
              </div>
              <Title level={2} style={{ color: theme.textPrimary, margin: 0 }}>
                {t('tutorial.strategyBuilderToolTitle')}
              </Title>
            </div>
            
            <Paragraph style={{ color: theme.textSecondary, fontSize: '16px', lineHeight: '1.8' }}>
              {t('tutorial.strategyBuilderToolDescription')}
            </Paragraph>
            
            <Card
              style={{
                background: theme.appBackground,
                borderColor: theme.surfaceBorder,
                marginTop: '24px'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Text strong style={{ color: theme.textPrimary, display: 'block', marginBottom: '16px', fontSize: '15px' }}>
                {t('tutorial.howToUse')}
              </Text>
              <ol style={{ 
                color: theme.textSecondary, 
                paddingLeft: '20px', 
                margin: 0,
                lineHeight: '2.2',
                fontSize: '14px'
              }}>
                <li>{t('tutorial.strategyBuilderStep1')}</li>
                <li>{t('tutorial.strategyBuilderStep2')}</li>
                <li>{t('tutorial.strategyBuilderStep3')}</li>
                <li>{t('tutorial.strategyBuilderStep4')}</li>
              </ol>
            </Card>
          </div>
        </div>
      )
    },
    {
      key: 'aiAgent',
      title: t('onboarding.aiAgentStep'),
      content: (
        <div style={{ display: 'flex', gap: '48px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* 视频区域 */}
          <div style={{ flex: '1 1 500px', minWidth: '300px' }}>
            <VideoPlayer type="aiAgent" lang={i18n.language} />
          </div>
          
          {/* 说明区域 */}
          <div style={{ flex: '1 1 400px', minWidth: '280px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '14px',
                  background: `linear-gradient(135deg, ${theme.accentPrimary}20 0%, ${theme.accentPrimary}40 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <RobotOutlined style={{ fontSize: '28px', color: theme.accentPrimary }} />
              </div>
              <Title level={2} style={{ color: theme.textPrimary, margin: 0 }}>
                {t('tutorial.aiAgentToolTitle')}
              </Title>
            </div>
            
            <Paragraph style={{ color: theme.textSecondary, fontSize: '16px', lineHeight: '1.8' }}>
              {t('tutorial.aiAgentToolDescription')}
            </Paragraph>
            
            <Card
              style={{
                background: theme.appBackground,
                borderColor: theme.surfaceBorder,
                marginTop: '24px'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Text strong style={{ color: theme.textPrimary, display: 'block', marginBottom: '16px', fontSize: '15px' }}>
                {t('tutorial.howToUse')}
              </Text>
              <ol style={{ 
                color: theme.textSecondary, 
                paddingLeft: '20px', 
                margin: 0,
                lineHeight: '2.2',
                fontSize: '14px'
              }}>
                <li>{t('tutorial.aiAgentStep1')}</li>
                <li>{t('tutorial.aiAgentStep2')}</li>
                <li>{t('tutorial.aiAgentStep3')}</li>
                <li>{t('tutorial.aiAgentStep4')}</li>
              </ol>
            </Card>
          </div>
        </div>
      )
    }
  ];

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.appBackground,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* 背景装饰 */}
      <div
        style={{
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '80%',
          height: '100%',
          background: `radial-gradient(circle, ${theme.accentPrimary}08 0%, transparent 70%)`,
          pointerEvents: 'none'
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-10%',
          width: '60%',
          height: '80%',
          background: `radial-gradient(circle, ${theme.accentPrimary}05 0%, transparent 60%)`,
          pointerEvents: 'none'
        }}
      />

      {/* 顶部栏 */}
      <div
        style={{
          padding: '24px 48px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          zIndex: 10
        }}
      >
        <Title level={4} style={{ color: theme.textPrimary, margin: 0 }}>
          Alphora AI
        </Title>
        <Space size="middle">
          {/* 语言切换器 */}
          <Space size="small">
            <GlobalOutlined style={{ color: theme.textSecondary, fontSize: '16px' }} />
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              style={{ width: 100 }}
              size="middle"
              variant="borderless"
            >
              <Option value="zh-CN">中文</Option>
              <Option value="en-US">English</Option>
            </Select>
          </Space>
          <Button type="text" onClick={handleSkip} style={{ color: theme.textSecondary }}>
            {t('onboarding.skip')}
          </Button>
        </Space>
      </div>

      {/* 主内容区 */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '0 48px 48px',
          position: 'relative',
          zIndex: 10
        }}
      >
        {/* 进度指示器 */}
        <div style={{ 
          maxWidth: '600px', 
          margin: '0 auto 48px',
          width: '100%'
        }}>
          <Steps
            current={currentStep}
            items={steps.map(step => ({ title: step.title }))}
            style={{ marginBottom: '16px' }}
          />
        </div>

        {/* 步骤内容 */}
        <div
          style={{
            flex: 1,
            maxWidth: '1200px',
            width: '100%',
            margin: '0 auto',
            animation: 'fadeIn 0.3s ease'
          }}
        >
          {steps[currentStep].content}
        </div>

        {/* 底部导航 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '1200px',
            width: '100%',
            margin: '48px auto 0',
            paddingTop: '24px',
            borderTop: `1px solid ${theme.surfaceBorder}`
          }}
        >
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => setCurrentStep(prev => prev - 1)}
            disabled={isFirstStep}
            style={{ 
              color: isFirstStep ? theme.textSecondary : theme.textPrimary,
              opacity: isFirstStep ? 0.5 : 1
            }}
          >
            {t('onboarding.previous')}
          </Button>

          <Text style={{ color: theme.textSecondary }}>
            {currentStep + 1} / {steps.length}
          </Text>

          {isLastStep ? (
            <Button
              type="primary"
              size="large"
              icon={<RocketOutlined />}
              onClick={handleComplete}
              style={{
                background: theme.accentPrimary,
                borderColor: theme.accentPrimary,
                height: '48px',
                padding: '0 32px',
                fontSize: '16px'
              }}
            >
              {t('onboarding.getStarted')}
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              onClick={() => setCurrentStep(prev => prev + 1)}
              style={{
                background: theme.accentPrimary,
                borderColor: theme.accentPrimary,
                height: '48px',
                padding: '0 32px',
                fontSize: '16px'
              }}
            >
              {t('onboarding.next')} <ArrowRightOutlined />
            </Button>
          )}
        </div>
      </div>

      {/* CSS 动画 */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

export default OnboardingPage;

